import {
  HHAlert,
  HHButton,
  HHLoadingButton,
  HHStack,
  HHTextField,
  HHTypography,
} from '@hinge-health/react-component-library';
import { yupResolver } from '@hookform/resolvers/yup';
import { Autocomplete } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useDynamicDrawerContext } from '../../../context/dynamic-drawer.context';
import {
  ClientType,
  InsurerType,
  PartnershipType,
  PaymentType,
  useGetProcedureCodesQuery,
} from '../../../types';
import { ClientSelect } from '../../form-fields/client-select';
import { PartnershipSelect } from '../../form-fields/partnership-select';
import {
  DEFAULT_PROCEDURE_CODE_FORM_VALUES,
  ProcedureCodeFormValues,
  ProcedureCodeValidationSchema,
  ValidatedProcedureCodeFormValues,
} from './validation-schema';

export const PROCEDURE_CODE_FORM_TEST_ID = 'procedure-code-form';
export const PROCEDURE_CODE_FORM_SUBMIT_TEST_ID =
  'procedure-code-form-submit-button';
export const PROCEDURE_CODE_FORM_CANCEL_TEST_ID =
  'procedure-code-form-cancel-button';

interface ProcedureCodeFormProps {
  onSubmit: (values: ValidatedProcedureCodeFormValues) => void | Promise<void>;
  initialValues?: ProcedureCodeFormValues;
  id?: number;
  insurers: Omit<InsurerType, 'eligibilityRef' | 'memberIdRequired'>[];
  insurersLoading: boolean;
  partnerships: PartnershipType[];
  partnershipsLoading: boolean;
  clients: ClientType[];
  clientsLoading: boolean;
  paymentTypes: PaymentType[];
  paymentTypesLoading: boolean;
}

export const ProcedureCodeForm = ({
  onSubmit,
  initialValues,
  id,
  insurers,
  insurersLoading,
  partnerships,
  partnershipsLoading,
  clients,
  clientsLoading,
  paymentTypes,
  paymentTypesLoading,
}: ProcedureCodeFormProps): JSX.Element => {
  const { onClose } = useDynamicDrawerContext();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const methods = useForm<ProcedureCodeFormValues>({
    mode: 'onChange',
    resolver: yupResolver(ProcedureCodeValidationSchema),
    defaultValues: DEFAULT_PROCEDURE_CODE_FORM_VALUES,
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    reset,
    trigger,
    watch,
  } = methods;

  useEffect(() => {
    if (initialValues) {
      reset(initialValues);
    }
  }, [initialValues, reset]);

  // ACCOUNTS-AND-PLAN-MIGRATION: partnershipId, insurerId, clientId are client/insurer/partnership-scoped, not resolvable via contract_id — will be addressed by the Accounts & Plan migration.
  const [partnershipId, insurerId, clientId, paymentTypeId] = watch([
    'partnershipId',
    'insurerId',
    'clientId',
    'paymentTypeId',
  ]);

  const paymentTypeName = paymentTypes.find(
    pt => pt.id === paymentTypeId,
  )?.name;

  const { data: duplicateCheckData } = useGetProcedureCodesQuery({
    variables: {
      partnershipId: partnershipId ?? undefined,
      insurerId: insurerId ?? undefined,
      clientId: clientId ?? undefined,
      paymentType: paymentTypeName,
    },
    skip: !paymentTypeId || !paymentTypeName,
    fetchPolicy: 'network-only',
  });

  const isDuplicateCombo = ((): boolean => {
    if (!paymentTypeId) return false;
    const existing = duplicateCheckData?.getProcedureCodes?.items ?? [];
    const exactMatches = existing.filter(
      item =>
        (item.partnershipId ?? null) === (partnershipId ?? null) &&
        (item.insurerId ?? null) === (insurerId ?? null) &&
        (item.clientId ?? null) === (clientId ?? null),
    );
    return id
      ? exactMatches.some(item => item.id !== id)
      : exactMatches.length > 0;
  })();

  const handleCancel = (): void => {
    reset();
    onClose();
  };

  const handleFormSubmit = async (
    values: ProcedureCodeFormValues,
  ): Promise<void> => {
    setIsSubmitting(true);
    try {
      await onSubmit(values as ValidatedProcedureCodeFormValues);
    } catch {
      setIsSubmitting(false);
    }
  };

  const isEditMode = !!initialValues;

  const sortedInsurers = useMemo(
    () => [...insurers].sort((a, b) => a.name.localeCompare(b.name)),
    [insurers],
  );

  const sortedPartnerships = useMemo(
    () => [...partnerships].sort((a, b) => a.name.localeCompare(b.name)),
    [partnerships],
  );

  const sortedClients = useMemo(
    () => [...clients].sort((a, b) => a.name.localeCompare(b.name)),
    [clients],
  );

  const sortedPaymentTypes = useMemo(
    () => [...paymentTypes].sort((a, b) => a.code.localeCompare(b.code)),
    [paymentTypes],
  );

  return (
    <FormProvider {...methods}>
      <form data-testid={PROCEDURE_CODE_FORM_TEST_ID}>
        <HHStack spacing={6} direction="column">
          <HHAlert hhVariant="outlined" severity="info">
            <HHTypography hhVariant="body">
              <strong>Valid combinations:</strong> Client only, Client +
              Insurer, Partnership + Insurer, Partnership only, or Insurer only
            </HHTypography>
          </HHAlert>
          <PartnershipSelect
            options={sortedPartnerships}
            loading={partnershipsLoading}
            disabled={isEditMode}
            error={errors.partnershipId}
            onAfterChange={(): void => {
              trigger(['insurerId', 'clientId']);
            }}
          />

          <Controller
            control={control}
            name="insurerId"
            render={({ field: { onChange, value } }): JSX.Element => (
              <Autocomplete
                value={
                  sortedInsurers.find(insurer => insurer.id === value) ?? null
                }
                options={sortedInsurers}
                getOptionLabel={(option): string => option.name}
                isOptionEqualToValue={(option, val): boolean =>
                  option.id === val?.id
                }
                onChange={(_, newValue): void => {
                  onChange(newValue?.id ?? null);
                  trigger(['partnershipId', 'clientId']);
                }}
                disabled={isEditMode}
                loading={insurersLoading}
                renderInput={(params): JSX.Element => (
                  <HHTextField
                    {...params}
                    hhVariant="variant-bypass"
                    variant="outlined"
                    label="Insurer"
                    error={!!errors.insurerId}
                    helperText={errors.insurerId?.message}
                    inputProps={{ ...params.inputProps, name: 'insurerId' }}
                    data-testid="procedure-code-form-insurer-input"
                  />
                )}
              />
            )}
          />

          <ClientSelect
            options={sortedClients}
            loading={clientsLoading}
            disabled={isEditMode}
            error={errors.clientId}
            onAfterChange={(): void => {
              trigger(['partnershipId', 'insurerId']);
            }}
          />

          <Controller
            control={control}
            name="paymentTypeId"
            render={({ field: { onChange, value } }): JSX.Element => (
              <Autocomplete
                value={sortedPaymentTypes.find(pt => pt.id === value) ?? null}
                options={sortedPaymentTypes}
                getOptionLabel={(option): string => option.code}
                isOptionEqualToValue={(option, val): boolean =>
                  option.id === val?.id
                }
                onChange={(_, newValue): void => onChange(newValue?.id ?? null)}
                loading={paymentTypesLoading}
                renderInput={(params): JSX.Element => (
                  <HHTextField
                    {...params}
                    hhVariant="variant-bypass"
                    variant="outlined"
                    label="Payment Type"
                    error={!!errors.paymentTypeId || isDuplicateCombo}
                    helperText={
                      errors.paymentTypeId?.message ??
                      (isDuplicateCombo
                        ? 'A CPT code already exists for this entity and payment type combination'
                        : undefined)
                    }
                    inputProps={{ ...params.inputProps, name: 'paymentTypeId' }}
                    data-testid="procedure-code-form-payment-type-input"
                  />
                )}
              />
            )}
          />

          <Controller
            control={control}
            name="cptCode"
            render={({ field: { onChange, value } }): JSX.Element => (
              <HHTextField
                hhVariant="variant-bypass"
                variant="outlined"
                label="CPT Code"
                value={value}
                onChange={onChange}
                error={!!errors.cptCode}
                helperText={errors.cptCode?.message}
                inputProps={{ role: 'textbox', name: 'cptCode' }}
                data-testid="procedure-code-form-cpt-code-input"
              />
            )}
          />

          <Controller
            control={control}
            name="modifier1"
            render={({ field: { onChange, value } }): JSX.Element => (
              <HHTextField
                hhVariant="variant-bypass"
                variant="outlined"
                label="Modifier 1"
                value={value}
                onChange={onChange}
                inputProps={{ role: 'textbox', name: 'modifier1' }}
                data-testid="procedure-code-form-modifier1-input"
              />
            )}
          />

          <Controller
            control={control}
            name="modifier2"
            render={({ field: { onChange, value } }): JSX.Element => (
              <HHTextField
                hhVariant="variant-bypass"
                variant="outlined"
                label="Modifier 2"
                value={value}
                onChange={onChange}
                inputProps={{ role: 'textbox', name: 'modifier2' }}
                data-testid="procedure-code-form-modifier2-input"
              />
            )}
          />

          <HHStack direction="row" spacing={2} justifyContent="flex-end">
            <HHButton
              hhVariant="variant-bypass"
              variant="text"
              onClick={handleCancel}
              type="button"
              disabled={isSubmitting}
              data-testid={PROCEDURE_CODE_FORM_CANCEL_TEST_ID}
            >
              Cancel
            </HHButton>
            <HHLoadingButton
              hhVariant="variant-bypass"
              variant="contained"
              type="submit"
              disabled={
                !isValid || !isDirty || isSubmitting || isDuplicateCombo
              }
              loading={isSubmitting}
              onClick={handleSubmit(handleFormSubmit)}
              data-testid={PROCEDURE_CODE_FORM_SUBMIT_TEST_ID}
            >
              Submit
            </HHLoadingButton>
          </HHStack>
        </HHStack>
      </form>
    </FormProvider>
  );
};
